import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, IconButton, Accordion, AccordionSummary,
  AccordionDetails, TextField, MenuItem, Paper, Stack, Snackbar, Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BookIcon from '@mui/icons-material/Book';
import studentApi from '../StudentApi';
import ActiveVouchersList from './CompactVoucherList';

export default function StudentVouchersModal({ studentId, visible, onClose, onVoucherActivated }) {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [examList, setExamList] = useState([]);
  const [subjectList, setSubjectList] = useState([]);
  const [topicList, setTopicList] = useState([]);

  const [selectedExam, setSelectedExam] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');

  const [couponCode, setCouponCode] = useState('');
  const [filteredVouchers, setFilteredVouchers] = useState([]);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const showToast = (message, severity = 'info') => setToast({ open: true, message, severity });

  // Fetch vouchers
  const fetchVouchers = async () => {
    setLoading(true);
    try {
      const res = await studentApi.get(`/student-vouchers/${studentId}`);
      const activeVouchers = res.data.success
        ? res.data.data.filter(v => v.status === 'active' && v.student_id === studentId)
        : [];
      setVouchers(activeVouchers);
    } catch (err) {
      console.error(err);
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && studentId) fetchVouchers();
  }, [visible, studentId]);

  // Fetch exams
  useEffect(() => {
    studentApi.get('/exams').then(res => setExamList(res.data)).catch(err => console.error(err));
  }, []);

  useEffect(() => {
    if (selectedExam) {
      studentApi.get(`/subjects?exam_id=${selectedExam}`)
        .then(res => setSubjectList(res.data))
        .catch(err => console.error(err));
    } else setSubjectList([]);
    setSelectedSubject('');
    setSelectedTopic('');
  }, [selectedExam]);

  useEffect(() => {
    if (selectedSubject) {
      studentApi.get(`/topics?subject_id=${selectedSubject}`)
        .then(res => setTopicList(res.data))
        .catch(err => console.error(err));
    } else setTopicList([]);
    setSelectedTopic('');
  }, [selectedSubject]);

  // Apply filters
  useEffect(() => {
    let filtered = vouchers;
    if (selectedExam) filtered = filtered.filter(v => v.exam_id && Number(v.exam_id) === Number(selectedExam));
    if (selectedSubject) filtered = filtered.filter(v => Number(v.subject_id) === Number(selectedSubject));
    if (selectedTopic) filtered = filtered.filter(v => Number(v.topic_id) === Number(selectedTopic));
    setFilteredVouchers(filtered);
  }, [selectedExam, selectedSubject, selectedTopic, vouchers]);

  // Activate voucher
  const handleActivateCoupon = async () => {
    if (!couponCode.trim()) {
      showToast('Please enter a coupon code.', 'warning');
      return;
    }

    try {
      const response = await studentApi.post(`/activate-voucher/${couponCode}`, {
        student_id: studentId,
        exam_id: selectedExam || null,
        subject_id: selectedSubject || null,
        topic_id: selectedTopic || null,
      });

      if (response.data.success) {
        const activatedVoucher = {
          id: response.data.data.id || Date.now(),
          code: couponCode,
          exam_id: selectedExam || null,
          subject_id: selectedSubject || null,
          topic_id: selectedTopic || null,
          status: 'active',
          From_Date: response.data.data.From_Date,
          To_Date: response.data.data.To_Date,
          type: 'exam',
          exam_name: selectedExam || '-',
          subject_name: selectedSubject || '-',
          topic_name: selectedTopic || '-',
        };

        setVouchers(prev => [activatedVoucher, ...prev]);
        setCouponCode('');
        showToast(response.data.message, 'success');

        // Notify parent to refresh
        if (onVoucherActivated) onVoucherActivated();
      } else {
        showToast(response.data.message, 'info');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Error activating coupon', 'error');
    }
  };

  return (
    <Dialog open={visible} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 'var(--border-radius-md)', overflow: 'hidden' } }}
    >
      <DialogTitle
        sx={{
          m: 0, p: 2, fontSize: '1.2rem', fontWeight: 'bold', color: '#fff',
          background: 'var(--primary-gradient)',
          borderTopLeftRadius: 'var(--border-radius-md)',
          borderTopRightRadius: 'var(--border-radius-md)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <span>Your Vouchers</span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: '#fff', backgroundColor: 'rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' }, width: 28, height: 28 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2, maxHeight: 550, overflowY: 'auto' }}>
        {/* Active Vouchers List */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Active Vouchers</Typography>
          <ActiveVouchersList studentId={studentId} onSetExam={(voucher) => console.log('Selected voucher:', voucher)} />
        </Paper>

        {/* Filters & Activate Voucher */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Filters & Vouchers</Typography>

          {/* Filters */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            flexWrap="wrap"
          >
            <TextField
              select
              size="small"
              label="Select Exam"
              value={selectedExam}
              onChange={e => setSelectedExam(e.target.value)}
              sx={{ flex: { xs: 1, sm: 'auto' }, minWidth: { xs: '100%', sm: 120 } }}
            >
              <MenuItem value="">All</MenuItem>
              {examList.map(ex => <MenuItem key={ex.id} value={ex.id}>{ex.title}</MenuItem>)}
            </TextField>

            <TextField
              select
              size="small"
              label="Select Subject"
              value={selectedSubject}
              onChange={e => setSelectedSubject(e.target.value)}
              sx={{ flex: { xs: 1, sm: 'auto' }, minWidth: { xs: '100%', sm: 120 } }}
            >
              <MenuItem value="">All</MenuItem>
              {subjectList.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>

            <TextField
              select
              size="small"
              label="Select Topic"
              value={selectedTopic}
              onChange={e => setSelectedTopic(e.target.value)}
              sx={{ flex: { xs: 1, sm: 'auto' }, minWidth: { xs: '100%', sm: 120 } }}
            >
              <MenuItem value="">All</MenuItem>
              {topicList.map(t => <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>)}
            </TextField>
          </Stack>

          {/* Voucher Code */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            mt={2}
          >
            <TextField
              size="small"
              label="Enter Voucher Code"
              value={couponCode}
              onChange={e => setCouponCode(e.target.value)}
              sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleActivateCoupon}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Validate
            </Button>
          </Stack>
        </Paper>


        {/* Filtered Vouchers List */}
        {loading ? (
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Loading...</Typography>
        ) : (selectedExam || selectedSubject || selectedTopic) ? (
          filteredVouchers.length === 0 ? (
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>No vouchers available for selected filters.</Typography>
          ) : (
            filteredVouchers.map(v => (
              <Accordion key={v.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0, pl: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>{v.code} ({(v.type || 'exam').toUpperCase()})</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1 }}>
                  <Stack spacing={0.5}>
                    {v.success_message && <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'green' }}>{v.success_message}</Typography>}
                    {v.remarks && <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'gray' }}>{v.remarks}</Typography>}
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center' }}><BookIcon sx={{ fontSize: 14, mr: 0.5 }} /> Exam: {v.exam_name ?? '-'}</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center' }}><BookIcon sx={{ fontSize: 14, mr: 0.5 }} /> Subject: {v.subject_name ?? '-'}</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center' }}><BookIcon sx={{ fontSize: 14, mr: 0.5 }} /> Topic: {v.topic_name ?? '-'}</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center' }}><BookIcon sx={{ fontSize: 14, mr: 0.5 }} /> Usage Limit: {v.usage_limit ?? '-'}</Typography>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))
          )
        ) : (
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Please select a filter to view vouchers.</Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 1 }}>
        <Button onClick={onClose} variant="contained" size="small">Close</Button>
      </DialogActions>

      {/* Snackbar Toast */}
      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast({ ...toast, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: '100%' }} variant="filled">{toast.message}</Alert>
      </Snackbar>
    </Dialog>
  );
}
